import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Layout, PageHeader, Container } from '../components/layout/Layout';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import { 
  Link2, Shield, Copy, CheckCircle,
  Code, Globe, ExternalLink, Trash2
} from 'lucide-react';

interface OneTimeLinkStatus {
  isActive: boolean;
  token: string | null;
  expiresAt: string | null;
  isExpired: boolean;
  isUsed: boolean;
}

export default function PageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState<any>(null);
  const [oneTimeStatus, setOneTimeStatus] = useState<OneTimeLinkStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadPage();
  }, [id]);

  const loadPage = async () => {
    try {
      setLoading(true);
      const pageData = await api.getSchedulingPage(id!);
      setPage(pageData);
      
      // Load one-time link status
      const status = await api.getOneTimeLinkStatus(id!) as OneTimeLinkStatus;
      setOneTimeStatus(status);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateOneTimeLink = async () => {
    try {
      setGenerating(true);
      const result = await api.generateOneTimeLink(id!, { expiresInHours: 24 }) as { token: string };
      setOneTimeStatus({
        isActive: true,
        token: result.token,
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        isExpired: false,
        isUsed: false,
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const revokeOneTimeLink = async () => {
    if (!confirm('Are you sure you want to revoke this one-time link?')) return;
    
    try {
      await api.revokeOneTimeLink(id!);
      setOneTimeStatus({
        isActive: false,
        token: null,
        expiresAt: null,
        isExpired: false,
        isUsed: false,
      });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getPublicUrl = () => {
    return `${window.location.origin}/schedule/${page?.slug}`;
  };

  const getOneTimeUrl = () => {
    if (!oneTimeStatus?.token) return '';
    return `${getPublicUrl()}?token=${oneTimeStatus.token}`;
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </Container>
      </Layout>
    );
  }

  if (error || !page) {
    return (
      <Layout>
        <Container>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
            {error || 'Page not found'}
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHeader
        title={page.title}
        description={page.description || 'Manage your scheduling page settings'}
        action={
          <div className="flex space-x-2">
            <Button
              variant="outline"
              onClick={() => navigate('/scheduling')}
            >
              Back to List
            </Button>
            <Button
              onClick={() => navigate(`/scheduling/${id}/edit`)}
            >
              Edit Page
            </Button>
          </div>
        }
      />

      <Container>
        <Tabs defaultValue="onetime" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
            <TabsTrigger value="onetime">
              <Shield className="w-4 h-4 mr-2" />
              One-Time Link
            </TabsTrigger>
            <TabsTrigger value="embed">
              <Code className="w-4 h-4 mr-2" />
              Embed
            </TabsTrigger>
            <TabsTrigger value="webhooks">
              <Globe className="w-4 h-4 mr-2" />
              Webhooks
            </TabsTrigger>
          </TabsList>

          <TabsContent value="onetime" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <CardTitle>One-Time Booking Link</CardTitle>
                </div>
                <CardDescription>
                  Create a secure, single-use link that expires after the first booking.
                  Perfect for sensitive meetings or confidential consultations.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {oneTimeStatus?.isActive ? (
                  <div className="space-y-4">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <div className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="font-medium text-green-900">Active One-Time Link</p>
                          <p className="text-sm text-green-700 mt-1">
                            This link will expire after the first booking is made
                            {oneTimeStatus.expiresAt && (
                              <> or at {new Date(oneTimeStatus.expiresAt).toLocaleString()}</>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">One-Time Link URL</label>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={getOneTimeUrl()}
                          readOnly
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                        />
                        <Button
                          variant="outline"
                          onClick={() => copyToClipboard(getOneTimeUrl())}
                          leftIcon={copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        >
                          {copied ? 'Copied!' : 'Copy'}
                        </Button>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(getOneTimeUrl())}
                        className="flex-1"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy Link
                      </Button>
                      <Button
                        variant="danger"
                        onClick={revokeOneTimeLink}
                        className="flex-1"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Revoke Link
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Link2 className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      No active one-time link
                    </h3>
                    <p className="text-gray-500 mb-6 max-w-md mx-auto">
                      Generate a secure one-time link that expires after the first booking.
                      Perfect for private meetings.
                    </p>
                    <Button
                      onClick={generateOneTimeLink}
                      isLoading={generating}
                      leftIcon={<Shield className="w-4 h-4" />}
                    >
                      Generate One-Time Link
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="embed" className="space-y-6">
            <EmbedWidget slug={page.slug} />
          </TabsContent>

          <TabsContent value="webhooks" className="space-y-6">
            <WebhookManager pageId={id!} />
          </TabsContent>
        </Tabs>
      </Container>
    </Layout>
  );
}

// Embed Widget Component
function EmbedWidget({ slug }: { slug: string }) {
  const [embedCode, setEmbedCode] = useState('');
  const [iframeCode, setIframeCode] = useState('');
  const [copied, setCopied] = useState<'embed' | 'iframe' | null>(null);
  const baseUrl = window.location.origin;

  useEffect(() => {
    const scriptEmbed = `<!-- Eureka Scheduling Widget -->
<div id="eureka-scheduling-${slug}"></div>
<script src="${baseUrl}/api/v1/embed/script/${slug}.js" async></script>
<!-- End Eureka Scheduling Widget -->`;

    const iframeEmbed = `<!-- Eureka Scheduling iFrame -->
<iframe 
  src="${baseUrl}/schedule/${slug}?embed=true" 
  width="100%" 
  height="600px" 
  style="border: 1px solid #e5e7eb; border-radius: 8px;"
  allowtransparency="true"
  scrolling="no"
></iframe>
<!-- End Eureka Scheduling iFrame -->`;

    setEmbedCode(scriptEmbed);
    setIframeCode(iframeEmbed);
  }, [slug, baseUrl]);

  const copyToClipboard = (type: 'embed' | 'iframe', text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <Code className="w-5 h-5 text-blue-600" />
          <CardTitle>Embed on Your Website</CardTitle>
        </div>
        <CardDescription>
          Add your scheduling page to any website by copying the code below.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              JavaScript Widget (Recommended)
            </label>
            <div className="relative">
              <textarea
                value={embedCode}
                readOnly
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard('embed', embedCode)}
                className="absolute top-2 right-2"
              >
                {copied === 'embed' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Best for modern websites. Automatically resizes and matches your site&apos;s styling.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              iFrame Embed
            </label>
            <div className="relative">
              <textarea
                value={iframeCode}
                readOnly
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard('iframe', iframeCode)}
                className="absolute top-2 right-2"
              >
                {copied === 'iframe' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Simple iframe that works everywhere. Fixed height.
            </p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-start space-x-3">
            <ExternalLink className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">Direct Link</p>
              <p className="text-sm text-blue-700 mt-1">
                Share this URL directly:{' '}
                <a 
                  href={`${baseUrl}/schedule/${slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  {baseUrl}/schedule/{slug}
                </a>
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Webhook Manager Component
function WebhookManager({ pageId }: { pageId: string }) {
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    url: '',
    events: ['booking.created'],
    description: '',
  });

  useEffect(() => {
    loadWebhooks();
  }, [pageId]);

  const loadWebhooks = async () => {
    try {
      const data = await api.getWebhooks(pageId) as { webhooks: any[] };
      setWebhooks(data.webhooks);
    } catch (err) {
      console.error('Failed to load webhooks');
    }
  };

  const createWebhook = async () => {
    try {
      await api.createWebhook(pageId, newWebhook);
      setShowForm(false);
      setNewWebhook({ url: '', events: ['booking.created'], description: '' });
      loadWebhooks();
    } catch (err) {
      console.error('Failed to create webhook');
    }
  };

  const deleteWebhook = async (webhookId: string) => {
    try {
      await api.deleteWebhook(webhookId);
      loadWebhooks();
    } catch (err) {
      console.error('Failed to delete webhook');
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Globe className="w-5 h-5 text-blue-600" />
            <CardTitle>Webhook Subscriptions</CardTitle>
          </div>
          <Button
            size="sm"
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Cancel' : 'Add Webhook'}
          </Button>
        </div>
        <CardDescription>
          Receive real-time notifications when bookings are created, updated, or cancelled.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {showForm && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Webhook URL</label>
              <input
                type="url"
                value={newWebhook.url}
                onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                placeholder="https://your-app.com/webhook"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Events</label>
              <select
                multiple
                value={newWebhook.events}
                onChange={(e) => {
                  const options = Array.from(e.target.selectedOptions, o => o.value);
                  setNewWebhook({ ...newWebhook, events: options });
                }}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="booking.created">Booking Created</option>
                <option value="booking.cancelled">Booking Cancelled</option>
                <option value="booking.rescheduled">Booking Rescheduled</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Description (optional)</label>
              <input
                type="text"
                value={newWebhook.description}
                onChange={(e) => setNewWebhook({ ...newWebhook, description: e.target.value })}
                placeholder="e.g., Send Slack notifications"
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
              <Button onClick={createWebhook}>
                Create Webhook
              </Button>
            </div>
          </div>
        )}

        {webhooks.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No webhooks configured
            </h3>
            <p className="text-gray-500 max-w-md mx-auto">
              Add a webhook to receive real-time notifications when someone books a meeting.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {webhooks.map((webhook) => (
              <div
                key={webhook.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-xl"
              >
                <div>
                  <p className="font-medium text-gray-900">{webhook.url}</p>
                  <p className="text-sm text-gray-500">
                    {webhook.events.join(', ')}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Badge variant={webhook.isActive ? 'success' : 'default'}>
                    {webhook.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteWebhook(webhook.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
